"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { FaLock, FaUserAlt, FaKey, FaGoogle, FaSpinner } from "react-icons/fa"
import { useNavigate } from "react-router-dom"
import HeroLaw from "@/components/start/HeroLaw"
import { useGoogleAuth } from "@/hooks/Auth/useGoogleAuth"
import { loginWithCredentials } from "@/services/Auth/loginService"
import Swal from "sweetalert2"
import Footer from "@/components/start/Footer";
import Cookies from "js-cookie"

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await loginWithCredentials(email, password)

      // Guardar tokens en localStorage
          // Guardar tokens en cookies
    Cookies.set("token", response.token, { expires: 1 }); // 1 día de expiración
    Cookies.set("refreshToken", response.rToken, { expires: 1 });
    Cookies.set("userData", JSON.stringify(response.user_data), { expires: 1 });
    Cookies.set("accountData", JSON.stringify(response.account_data), { expires: 1 });

      // Mostrar alerta de éxito
      Swal.fire({
        title: "¡Inicio de sesión exitoso!",
        text: `Bienvenido ${response.user_data.name || "de nuevo"}`,
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "var(--navy-blue)",
      })

      // Redireccionar según el rol (user_type está en account_data)
      console.log(response.account_data.user_type)
      redirectBasedOnRole(response.account_data.user_type)
    } catch (error) {
      // Mostrar alerta de error
      Swal.fire({
        title: "Error de inicio de sesión",
        text: error.message || "Credenciales incorrectas. Por favor, inténtelo de nuevo.",
        icon: "error",
        confirmButtonText: "Intentar de nuevo",
        confirmButtonColor: "var(--navy-blue)",
      })
      console.error("Error de inicio de sesión:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleConsultaClick = () => {
    const form = document.getElementById("loginForm")
    form.scrollIntoView({ behavior: "smooth" })
  }

  const { login, user, error } = useGoogleAuth()

  const handleGoogleLogin = async () => {
    try {
      await login()
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: 'No se pudo iniciar sesión con Google',
        icon: 'error',
        confirmButtonText: 'Intentar de nuevo',
        confirmButtonColor: 'var(--navy-blue)',
      })
    }
  }
  
  
  

  const redirectBasedOnRole = (role) => {
    switch (role?.toLowerCase()) {
      case "Client":
        navigate("/dashboard/client")
        break
      case "lawyer":
      case "abogado":
        navigate("/dashboard/lawyer")
        console.log("login")
        break
      case "Admin":
        navigate("/dashboard/admin")
        break
      default:
        navigate("/dashboard")
        console.log("defaultLogin")
        break
    }
  }

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  return (
    <>
      <HeroLaw
        titulo="Accede a Tu Cuenta Legal"
        subtitulo="Inicia sesión y gestiona tus casos de manera eficiente"
        imagenSrc="https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
        imagenAlt="Oficina de abogados moderna"
        botonTexto="Iniciar Sesión"
        onBotonClick={handleConsultaClick}
      />

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-2xl"
          id="loginForm"
        >
          <motion.div variants={itemVariants}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="mx-auto h-24 w-24 bg-gradient-to-r from-[var(--navy-blue)] to-[var(--navy-blue-light)] rounded-full flex items-center justify-center shadow-lg"
            >
              <FaUserAlt className="h-12 w-12 text-white" />
            </motion.div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Acceso a su cuenta</h2>
            <p className="mt-2 text-center text-sm text-gray-600">Bienvenido al portal de clientes</p>
          </motion.div>
          <motion.form className="mt-8 space-y-6" onSubmit={handleSubmit} variants={itemVariants}>
            <input type="hidden" name="remember" value="true" />
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Correo electrónico
                </label>
                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUserAlt className="h-5 w-5 text-indigo-500" />
                  </div>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[var(--gold)] focus:border-[var(--gold)] focus:z-10 sm:text-sm"
                    placeholder="Correo electrónico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </motion.div>
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Contraseña
                </label>
                <motion.div whileFocus={{ scale: 1.02 }} className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaKey className="h-5 w-5 text-indigo-500" />
                  </div>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[var(--gold)] focus:border-[var(--gold)] focus:z-10 sm:text-sm"
                    placeholder="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </motion.div>
              </div>
            </div>

            <motion.div variants={itemVariants} className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[var(--gold)] focus:ring-[var(--gold)] border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Recordarme
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-[var(--gold)] hover:text-[var(--gold-dark)] ">
                  ¿Olvidó su contraseña?
                </a>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.button
                whileHover={{ scale: isLoading ? 1 : 1.05 }}
                whileTap={{ scale: isLoading ? 1 : 0.95 }}
                type="submit"
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-[var(--gold)] to-[var(--gold-dark)] hover:from-[var(--gold)] hover:to-[var(--gold-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--gold)] transition duration-150 ease-in-out"
                disabled={isLoading}
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  {isLoading ? (
                    <FaSpinner className="h-5 w-5 text-[var(--navy-blue)] animate-spin" aria-hidden="true" />
                  ) : (
                    <FaLock
                      className="h-5 w-5 text-[var(--navy-blue)] group-hover:text-[var(--navy-blue-light)]"
                      aria-hidden="true"
                    />
                  )}
                </span>
                {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
              </motion.button>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 mb-4">O inicia sesión con:</p>
                <motion.button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-md hover:bg-gray-50 transition duration-300 flex items-center justify-center shadow-sm"
                  whileHover={{ scale: isLoading ? 1 : 1.05 }}
                  whileTap={{ scale: isLoading ? 1 : 0.95 }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FaGoogle className="mr-2 text-red-500" />
                      Iniciar sesión con Google
                    </>
                  )}
                </motion.button>
                {error && <p className="text-red-500 text-sm mt-2 bg-red-50 p-2 rounded-lg">{error}</p>}
                {user && (
                  <p className="text-green-500 text-sm mt-2 bg-green-50 p-2 rounded-lg">¡Bienvenido, {user.account_data.username}!</p>
                )}
              </div>
            </motion.div>
          </motion.form>
        </motion.div>
      </div>
      <Footer />
    </>
  )
}

export default Login

